import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Crosshair, Check, Copy, Loader, ShieldCheck,
  Clock, CheckCircle, XCircle, AlertTriangle,
} from 'lucide-react';
import { usePentest } from '../contexts/PentestContext';

const ENVIRONMENTS = ['staging', 'production', 'development', 'qa'];

/**
 * Reusable ownership-verification panel for a single target. Exported so the
 * start-pentest wizard can render the same flow. Shows the method/token/
 * instructions returned by verify/start and a "Check" button for verify/check.
 */
export const TargetVerificationPanel = ({ target }) => {
  const {
    verifyInfoMap, verifiedMap, verifyingMap, startVerification, checkVerification,
  } = usePentest();
  const [copied, setCopied] = useState(false);

  const info = verifyInfoMap[target.id];
  const verified = verifiedMap[target.id] ?? !!target.verified;
  const busy = !!verifyingMap[target.id];

  const handleCopy = () => {
    if (!info?.token) return;
    navigator.clipboard.writeText(info.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (verified) {
    return (
      <div className="flex items-center gap-2 text-green-600 text-sm">
        <ShieldCheck className="w-4 h-4" />
        Ownership verified
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => startVerification(target.id)}
          disabled={busy}
          className="px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
        >
          {busy ? <Loader className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {info ? 'Restart Verification' : 'Verify Ownership'}
        </button>
        {info && (
          <button
            onClick={() => checkVerification(target.id)}
            disabled={busy}
            className="px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            Check
          </button>
        )}
      </div>

      {info && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          {info.method && (
            <p className="text-xs text-gray-600">
              Method: <span className="font-medium text-gray-800">{info.method}</span>
            </p>
          )}
          {info.instructions && (
            <p className="text-sm text-gray-700">{info.instructions}</p>
          )}
          {info.token && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Verification token:</p>
              <div className="flex items-center gap-2">
                <code className="px-3 py-1.5 bg-gray-100 rounded text-sm font-mono text-gray-900 break-all flex-1">
                  {info.token}
                </code>
                <button onClick={handleCopy} className="p-2 text-gray-400 hover:text-gray-600" title="Copy token">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Add-target inline form.
const AddTargetForm = ({ onAdd }) => {
  const [targetUrl, setTargetUrl] = useState('');
  const [environment, setEnvironment] = useState(ENVIRONMENTS[0]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!targetUrl.trim()) {
      setFormError('Target URL is required');
      return;
    }
    setSaving(true);
    const created = await onAdd({ targetUrl: targetUrl.trim(), environment });
    setSaving(false);
    if (created) {
      setTargetUrl('');
      setEnvironment(ENVIRONMENTS[0]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-100 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Target URL</label>
          <input
            type="url"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder="https://staging.example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            disabled={saving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            disabled={saving}
          >
            {ENVIRONMENTS.map((env) => (
              <option key={env} value={env}>{env}</option>
            ))}
          </select>
        </div>
      </div>
      {formError && <p className="text-sm text-red-600">{formError}</p>}
      <button
        type="submit"
        disabled={saving}
        className="px-4 py-2 min-h-[44px] bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
      >
        {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Add Target
      </button>
    </form>
  );
};

const statusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'complete' || s === 'completed') {
    return (
      <span className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-sm">
        <CheckCircle className="w-4 h-4" />Completed
      </span>
    );
  }
  if (s === 'running' || s === 'in_progress' || s === 'scanning') {
    return (
      <span className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-sm">
        <Clock className="w-4 h-4" />Running
      </span>
    );
  }
  if (s === 'failed' || s === 'error') {
    return (
      <span className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full text-sm">
        <XCircle className="w-4 h-4" />Failed
      </span>
    );
  }
  return (
    <span className="px-3 py-1 bg-gray-50 text-gray-600 border border-gray-200 rounded-full text-sm">
      {status || 'Unknown'}
    </span>
  );
};

const ApplicationDetailPage = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const {
    getApplication, loadApplications, createTarget, updateApplication,
    creatingApplication, cancelPentest, cancelling, error,
  } = usePentest();

  const [loading, setLoading] = useState(true);
  const [editingRepo, setEditingRepo] = useState(false);
  const [repoDraft, setRepoDraft] = useState('');

  // The list endpoint carries targets + pentest history, so refetch it and read
  // this application out of the result.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      await loadApplications();
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [loadApplications]);

  // Derived during render so "not found" can't flash between loading ending
  // and the application being picked out of the refreshed list.
  const application = getApplication(appId) || null;

  const handleAddTarget = async (data) => {
    const created = await createTarget(appId, data);
    return created;
  };

  if (loading && !application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Application not found</h2>
          <button
            onClick={() => navigate('/applications')}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Back to Applications
          </button>
        </div>
      </div>
    );
  }

  const targets = Array.isArray(application.targets) ? application.targets : [];
  const pentests = Array.isArray(application.pentests) ? application.pentests : [];

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/applications"
          className="inline-flex items-center text-teal-600 hover:text-teal-700 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Applications
        </Link>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Crosshair className="w-7 h-7 text-teal-600 flex-shrink-0" />
              {application.name}
            </h1>
            {editingRepo ? (
              <form
                className="mt-2 flex flex-col sm:flex-row gap-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const updated = await updateApplication(appId, { repoUrl: repoDraft.trim() });
                  if (updated) setEditingRepo(false);
                }}
              >
                <input
                  type="url"
                  autoFocus
                  value={repoDraft}
                  onChange={(e) => setRepoDraft(e.target.value)}
                  placeholder="https://github.com/org/repo"
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  disabled={creatingApplication}
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={creatingApplication || !repoDraft.trim()}
                    className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingRepo(false)}
                    disabled={creatingApplication}
                    className="text-gray-600 hover:text-gray-800 text-sm px-3 py-2"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {application.repoUrl ? (
                  <p className="text-gray-500 text-sm break-all">{application.repoUrl}</p>
                ) : (
                  <p className="text-sm text-amber-700">No repository linked</p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setRepoDraft(application.repoUrl || '');
                    setEditingRepo(true);
                  }}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium underline"
                >
                  {application.repoUrl ? 'Change' : 'Add repository'}
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate(`/applications/${appId}/pentest/new`)}
            className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2 min-h-[44px] rounded-lg shadow transition"
          >
            Start Pentest
          </button>
        </div>

        {error && (
          <div className="mb-6 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">{error}</div>
        )}

        {!application.repoUrl && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">A linked repository is required to run a pentest.</p>
              <p className="mt-1">
                Pentests are <span className="font-medium">white-box</span>: the engine reads your
                source code to find attack paths, then proves them against the running app. A
                target URL alone is not enough. The repository is cloned, mounted read-only into
                an ephemeral scan container, and its contents are sent to your configured AI
                provider as analysis context — so only link one you are authorized to share on
                those terms.
              </p>
            </div>
          </div>
        )}

        {/* Targets */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 sm:p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Targets & Environments</h2>
          <div className="space-y-4 mb-6">
            {targets.length === 0 ? (
              <p className="text-gray-500 text-sm">No targets yet. Add a running URL below to get started.</p>
            ) : (
              targets.map((target) => (
                <div key={target.id} className="border border-gray-100 rounded-lg p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="font-mono text-sm text-gray-900 break-all">{target.targetUrl}</div>
                      <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-gray-100 text-gray-700 uppercase">
                        {target.environment || 'unknown'}
                      </span>
                    </div>
                    <div className="sm:text-right sm:flex-shrink-0">
                      <TargetVerificationPanel target={target} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <AddTargetForm onAdd={handleAddTarget} />
        </div>

        {/* Pentest history */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">Pentest History</h2>
          </div>
          {pentests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No pentests run yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Findings</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pentests.map((pt) => {
                    const pid = pt.pentestId || pt.id;
                    return (
                      <tr
                        key={pid}
                        onClick={() => navigate(`/pentest/${pid}/results`)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {pt.createdAt || pt.started_at
                            ? new Date(pt.createdAt || pt.started_at).toLocaleString()
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 break-all max-w-xs">
                          {pt.targetUrl || pt.targetId || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{statusBadge(pt.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                          {pt.findingsCount ?? pt.findings_count ?? '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {/* Only in-flight runs can be cancelled. Offered here as well as on
                              the results page so a queue of stacked-up runs can be cleared
                              without opening each one -- the dast lane runs one at a time, so
                              a few queued runs block it for hours. */}
                          {(pt.status === 'queued' || pt.status === 'running') && (
                            <button
                              type="button"
                              disabled={cancelling}
                              onClick={async (e) => {
                                e.stopPropagation();  // the row itself navigates
                                // Reloading on failure would clear the cancel error (loadApplications
                                // resets it), hiding why the run is still listed.
                                const result = await cancelPentest(pid);
                                if (result) await loadApplications();
                              }}
                              className="text-sm text-gray-500 hover:text-red-600 underline disabled:text-gray-300"
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailPage;
